import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Sparkles, Briefcase, Building2, User, Eye, Ear,
  ShieldCheck, FileCheck, HeartPulse, Network, MailCheck, Calendar, Phone,
} from "lucide-react";

// ============================================================
// Section wrapper
// ============================================================
export function Section({ variant = "grid", eyebrow, title, children }) {
  const Icon = eyebrow?.icon;
  const toneClass = eyebrow?.tone === "mint"
    ? "text-[var(--mc-mint)]"
    : eyebrow?.tone === "warn"
      ? "text-[#B45309]"
      : "text-[#0284C7]";
  const bgClass = variant === "soft" ? "hex-soft" : "hex-grid";
  return (
    <section className={`${bgClass} py-14 lg:py-20`}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          {eyebrow && (
            <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] ${toneClass} mb-3`}>
              {Icon && <Icon size={13} />} {eyebrow.label}
            </div>
          )}
          <h2 className="font-display text-white text-3xl sm:text-4xl font-semibold leading-tight">
            {title}
          </h2>
        </div>
        {children}
      </div>
    </section>
  );
}

// ============================================================
// FAQ list (accordion + structured-data driven by parent)
// ============================================================
export function FAQList({ items, testidPrefix = "faq" }) {
  return (
    <div className="max-w-3xl mx-auto grid gap-3">
      {items.map((f, i) => (
        <details key={f.q} className="mc-card p-5 group" data-testid={`${testidPrefix}-${i}`}>
          <summary className="cursor-pointer list-none flex items-start justify-between gap-3 text-[14.5px] text-white font-medium leading-snug">
            <span>{f.q}</span>
            <ArrowRight size={16} className="shrink-0 mt-0.5 text-[#0284C7] transition group-open:rotate-90" />
          </summary>
          <p className="mt-3 text-[13.5px] text-[var(--mc-muted)] leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

// ============================================================
// Brand image block (personal vs organizational)
// ============================================================
export function BrandImageBlock() {
  return (
    <Section variant="soft" eyebrow={{ icon: Briefcase, label: "Image professionnelle", tone: "mint" }}
             title="Une adresse qui reflète votre activité">
      <div className="max-w-2xl mx-auto">
        <div className="mc-card p-7 lg:p-8 border-[rgba(110,231,197,0.35)] bg-[rgba(110,231,197,0.05)]">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-mint)] mb-4 inline-flex items-center gap-2">
            <Building2 size={12} /> Boîte organisationnelle MetaConnect
          </div>
          <code className="block text-[15px] lg:text-[16px] font-mono text-white break-all px-4 py-3 rounded-lg bg-[#0F2B5B] border border-[#0F2B5B]">
            nom-souhaité@opticiens.mssante.fr
          </code>
          <p className="mt-4 text-[13px] text-white/85 leading-relaxed">
            Sous-domaine métier exclusif (<code className="font-mono">@opticiens</code>,{" "}
            <code className="font-mono">@audioprothesistes</code>,{" "}
            <code className="font-mono">@ohmyvue</code>, etc.) — votre adresse reflète votre
            activité, rattachée à votre structure (SIRET / FINESS), partagée par votre équipe avec
            des droits différenciés (responsable, cotitulaire, délégataire).
          </p>
          <p className="mt-3 text-[12.5px] text-[var(--mc-muted)] leading-relaxed">
            Modèle <strong className="text-white">recommandé par l'Agence du Numérique en Santé</strong>{" "}
            pour les exercices en équipe et les structures.
          </p>
        </div>
      </div>
    </Section>
  );
}

// ============================================================
// OhMyVue / OhMyOuie integration block
// ============================================================
export function OhMyVueIntegrationBlock({ title, description, focusArea, focusIcon: FocusIcon = Eye }) {
  return (
    <Section variant="grid" eyebrow={{ icon: Network, label: "Écosystème MetaConnect", tone: "mint" }}
             title={title || "MSSanté + OhMyVue + OhMyOuie — un écosystème de coordination du soin"}>
      <div className="max-w-4xl mx-auto">
        <p className="text-[15px] text-[var(--mc-muted)] leading-relaxed text-center mb-8">
          {description}
        </p>
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="mc-card p-5 border-[rgba(110,231,197,0.30)] bg-[rgba(110,231,197,0.04)]">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.15)] border border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] mb-3">
              <Eye size={18} />
            </div>
            <div className="font-display text-white text-[15px] mb-1">OhMyVue.fr</div>
            <p className="text-[12px] text-[var(--mc-muted)] leading-relaxed">
              Plateforme du parcours visuel (opticien ↔ ophtalmologiste ↔ orthoptiste).
            </p>
          </div>
          <div className="mc-card p-5 border-[rgba(168,140,255,0.30)] bg-[rgba(168,140,255,0.04)]">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(168,140,255,0.15)] border border-[rgba(168,140,255,0.4)] text-[#d2c2ff] mb-3">
              <Ear size={18} />
            </div>
            <div className="font-display text-white text-[15px] mb-1">OhMyOuie</div>
            <p className="text-[12px] text-[var(--mc-muted)] leading-relaxed">
              Plateforme du parcours auditif (audioprothésiste ↔ ORL ↔ médecin traitant).
            </p>
          </div>
          <div className="mc-card p-5 border-[rgba(127,179,255,0.30)] bg-[rgba(45,123,255,0.04)]">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0284C7] mb-3">
              <ShieldCheck size={18} />
            </div>
            <div className="font-display text-white text-[15px] mb-1">MSSanté MetaConnect</div>
            <p className="text-[12px] text-[var(--mc-muted)] leading-relaxed">
              Le canal sécurisé qui transporte les données entre tous les acteurs.
            </p>
          </div>
        </div>
        <div className="mc-card p-6 lg:p-7">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-mint)] mb-3 inline-flex items-center gap-2">
            <Sparkles size={12} /> Devoir d'information automatique — {focusArea}
          </div>
          <p className="text-[14px] text-white/90 leading-relaxed mb-4">
            Plus de ressaisie, plus d'oublis : le devoir d'information entre professionnels est
            respecté <strong className="text-[var(--mc-mint)]">automatiquement</strong>, et chaque
            acteur du parcours gagne du temps.
          </p>
          <ul className="grid sm:grid-cols-2 gap-2.5 mb-5">
            {[
              "Information automatique des prescripteurs lors des actes",
              "Traçabilité complète pour la coordination des soins",
              "Interopérable avec vos logiciels métier (Maincare, etc.)",
              "Conforme au cadre réglementaire et au secret médical",
              "Gain de temps administratif pour toutes les équipes",
              "Meilleure expérience patient grâce au suivi coordonné",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-[13px] text-white/85">
                <Check size={14} className="text-[var(--mc-mint)] mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="https://ohmyvue.fr" target="_blank" rel="noopener noreferrer"
               data-testid="persona-link-ohmyvue"
               className="btn-ghost px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <FocusIcon size={13} /> Découvrir OhMyVue.fr
            </a>
            <Link to="/demo" data-testid="persona-cta-ecosystem"
                  className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <Phone size={13} /> Pour en savoir plus, contactez-nous
            </Link>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ============================================================
// Final CTA block
// ============================================================
export function FinalCTABlock() {
  return (
    <section className="hex-grid py-16 lg:py-24" aria-labelledby="cta-h2">
      <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)] mb-4">
          <ShieldCheck size={13} /> Passez à l'action
        </div>
        <h2 id="cta-h2" className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
          Lancez votre messagerie <span className="text-[var(--mc-mint)]">MSSanté</span> aujourd'hui
        </h2>
        <p className="mt-4 text-[15px] text-[var(--mc-muted)] max-w-xl mx-auto leading-relaxed">
          Rejoignez les professionnels de santé qui ont fait le choix d'une messagerie
          organisationnelle moderne, sécurisée et interopérable.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/mssante/inscription" data-testid="persona-final-cta-create"
                className="btn-primary px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
            <MailCheck size={15} /> Créer ma boîte MSSanté
          </Link>
          <Link to="/demo" data-testid="persona-final-cta-demo"
                className="btn-ghost px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
            <Calendar size={15} /> Demander une démo
          </Link>
          <a href="mailto:contact@metaconnect.fr?subject=Rappel%20expert%20MSSanté"
             data-testid="persona-final-cta-callback"
             className="btn-ghost px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2">
            <Phone size={15} /> Être rappelé par un expert
          </a>
        </div>
        <p className="mt-6 text-[12px] text-[var(--mc-muted)]">
          Sans engagement · Tarification transparente · Mise en service sous 24-48 h
        </p>
      </div>
    </section>
  );
}
